import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/Configuration.css";
import {
  Button,
  Stack,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
} from "@mui/material";
import { Colors } from "../../Styles/Colors";
import ImagePaths from "../../Utility/ImagePaths";
import { DeletePopup } from "../../Components/Common Components/CommonComponents";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import SearchIcon from "@mui/icons-material/Search";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import Loader from "../../Components/Loader";

const t = (key) => {
  const labels = {
    SNo: "S.No",
    LaboratoryName: "Laboratory Name",
    REGNumber: "REG No",
    Address: "Address",
    OutsourceConfigration: "Outsource Configuration",
    OutsourceLaboratoryName: "Outsource Laboratory Name",
    RegistrationNo: "Registration No",
    GSTNNo: "GSTN No",
    Address1: "Address 1",
    Address2: "Address 2",
    Pincode: "Pincode",
    City: "City",
    State: "State",
    Clear: "Clear",
    Save: "Save",
    NoRecordsFound: "No Records Found",
    ConfirmRemovalOutsourceConfig: "Are you sure you want to remove this outsource configuration?",
    effectEntireConfig: "It will affect the entire configuration.",
    linkedOutsource: "Linked outsource records will be affected.",
    Fillmandatory: "Please fill all mandatory fields.",
    GSTNMaxLengthExceeded: "GSTN number must not exceed 15 characters.",
  };
  return labels[key] ?? key;
};

export default function OutSourceConfiguration() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [outSourceConfigData, setOutSourceConfigData] = useState([]);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [outsourceLabName, setOutsourceLabName] = useState("");
  const [GSTNno, setGSTNno] = useState("");
  const [regNo, setRegNo] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [selectState, setSelectState] = useState("");
  const [selectedEditID, setSelectedEditID] = useState("");
  const [deletedPopup, setDeletedPopup] = useState(false);
  const [deletedID, setDeletedID] = useState("");
  const [SearchText, setSearchText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [isLoader, setLoader] = useState(false);

  const editAccess = CheckAccessFunc("Histopathology", "Settings", "Outsource Configuration", null, "Tab")?.editAccess;

  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  };

  const getOutSourceConfigData = useCallback(() => {
    try {
      setLoader(true);
      RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_OUTSOURCE_CONFIG)
        .then((response) => {
          if (response.data.status === "success") {
            setOutSourceConfigData(response.data.data ?? []);
          }
          setLoader(false);
        })
        .catch((e) => {
          setLoader(false);
          setIsErrorMsg(true);
          setIsErrorMsgText(e.response?.data?.message ?? e.message);
        });
    } catch (e) {
      setLoader(false);
      setIsErrorMsg(true);
      setIsErrorMsgText(e.message);
    }
  }, []);

  useEffect(() => {
    getOutSourceConfigData();
  }, [getOutSourceConfigData]);

  const postOutSourceConfigData = () => {
    try {
      const data = {
        outsource_laboratory_name: outsourceLabName ? outsourceLabName.replace(/\s+/g, " ").trim() : "",
        reg_number: regNo ? Number(regNo) : null,
        gstn_no: GSTNno ? GSTNno.replace(/\s+/g, " ").trim() : "",
        address1: address1 ? address1.replace(/\s+/g, " ").trim() : "",
        address2: address2 ? address2.replace(/\s+/g, " ").trim() : "",
        city: city ? city.replace(/\s+/g, " ").trim() : "",
        pincode: pincode ? +pincode : "",
        state: selectState ? selectState.replace(/\s+/g, " ").trim() : "",
      };
      if (selectedEditID) {
        data.id = selectedEditID;
        const existing = outSourceConfigData.find((d) => d.id === selectedEditID);
        data.is_active = existing?.is_active ?? true;
      }
      setDisableBtn(true);
      RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_OUTSOURCE_CONFIG)
        .then((response) => {
          if (response.data.status === "success") {
            setSuccessMsg(true);
            setSuccessMsgText(response.data.message);
            getOutSourceConfigData();
            setOutsourceLabName("");
            setRegNo("");
            setGSTNno("");
            setAddress1("");
            setAddress2("");
            setPincode("");
            setCity("");
            setSelectState("");
            setSelectedEditID("");
            setDisableBtn(false);
          }
        })
        .catch((e) => {
          setIsErrorMsg(true);
          setIsErrorMsgText(e.response?.data?.message ?? e.message);
          setDisableBtn(false);
        });
    } catch (e) {
      setIsErrorMsg(true);
      setIsErrorMsgText(e.message);
      setDisableBtn(false);
    }
  };

  const deleteOutsourceConfigData = (id) => {
    try {
      const urlWithId = `${Serviceurls.HISTOPATHOLOGY_OUTSOURCE_CONFIG}?id=${id}`;
      RestAPIService.delete(urlWithId)
        .then((response) => {
          if (response.data.status === "success") {
            setSuccessMsg(true);
            setSuccessMsgText(response.data.message);
            getOutSourceConfigData();
          }
        })
        .catch((e) => {
          setIsErrorMsg(true);
          setIsErrorMsgText(e.response?.data?.message ?? e.message);
        });
    } catch (e) {
      setIsErrorMsg(true);
      setIsErrorMsgText(e.message);
    }
  };

  const editOutSourceConfigData = (item, index) => {
    setSelectedEditID(item.id);
    setOutsourceLabName(item.outsource_laboratory_name ?? "");
    setRegNo(item.reg_number ? String(item.reg_number) : "");
    setGSTNno(item.gstn_no ?? "");
    setAddress1(item.address1 ?? "");
    setAddress2(item.address2 ?? "");
    setCity(item.city ?? "");
    setPincode(item.pincode ?? "");
    setSelectState(item.state ?? "");
    setSelectedIndex(index);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const textInputcheck = (e) => {
    const { name, value } = e.target;
    if (value.length <= 150) {
      if (name === "outsourceLabName") setOutsourceLabName(value);
      else if (name === "address1") setAddress1(value);
      else if (name === "address2") setAddress2(value);
      else if (name === "city") setCity(value);
      else if (name === "selectState") setSelectState(value);
    }
  };

  const numInputcheck = (e) => {
    const { name, value } = e.target;
    const re = /^[0-9\b]+$/;
    if (value === "" || re.test(value)) {
      if (name === "regNo") setRegNo(value);
      else if (name === "pincode") setPincode(value);
    }
  };

  const handleGSTNInput = (e) => {
    const { value } = e.target;
    if (value === "" || /^[a-zA-Z0-9]{0,15}$/.test(value)) setGSTNno(value);
  };

  const renderTextBox = (label, width, value, name) => (
    <div>
      <TextField
        size="small"
        sx={{ width }}
        label={label}
        name={name}
        value={value}
        variant="outlined"
        inputProps={{ maxLength: name === "regNo" ? 7 : name === "pincode" ? 6 : undefined }}
        onChange={name === "regNo" || name === "pincode" ? numInputcheck : textInputcheck}
      />
    </div>
  );

  const renderGSTNTextBox = (label, width) => (
    <div>
      <TextField
        size="small"
        sx={{ width }}
        label={label}
        name="GSTNno"
        value={GSTNno}
        variant="outlined"
        onChange={handleGSTNInput}
        inputProps={{ maxLength: 15 }}
      />
    </div>
  );

  const filtereddata = outSourceConfigData.filter(
    (data) =>
      (data.outsource_laboratory_name ?? "").toLowerCase().includes(SearchText.toLowerCase()) ||
      (data.reg_number != null ? String(data.reg_number).toLowerCase().includes(SearchText.toLowerCase()) : false) ||
      (data.address1 ?? "").toLowerCase().includes(SearchText.toLowerCase()) ||
      (data.address2 ?? "").toLowerCase().includes(SearchText.toLowerCase()) ||
      (data.city ?? "").toLowerCase().includes(SearchText.toLowerCase()) ||
      (data.pincode != null ? String(data.pincode).toLowerCase().includes(SearchText.toLowerCase()) : false)
  );

  const clearValue = () => {
    setOutsourceLabName("");
    setRegNo("");
    setGSTNno("");
    setAddress1("");
    setAddress2("");
    setPincode("");
    setCity("");
    setSelectState("");
    setSelectedEditID("");
    setSelectedIndex("");
  };

  const validCheck = () => {
    if (!outsourceLabName?.trim() || !city?.trim() || !selectState?.trim()) return t("Fillmandatory");
    if (GSTNno && GSTNno.length > 15) return t("GSTNMaxLengthExceeded");
    return "";
  };

  const onSave = () => {
    const inValid = validCheck();
    if (inValid) {
      setIsErrorMsg(true);
      setIsErrorMsgText(inValid);
    } else {
      postOutSourceConfigData();
    }
  };

  const PopupClose = () => setDeletedPopup(false);
  const removeDataList = () => {
    deleteOutsourceConfigData(deletedID);
    setDeletedPopup(false);
  };

  const slice = filtereddata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="eMed_TestConfig_MainContainer">
      <div className="eMed_TestConfig_Con">
        <div className="eMed_TestConfig_leftSide">
          <Paper elevation={0}>
            <Box className="eMed_Search_Box_">
              <TextField
                variant="standard"
                placeholder="Search"
                value={SearchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "15vw" }}
              />
            </Box>
            <TableContainer className="eMed_ConfigTable_Paper">
              <Table stickyHeader size="small">
                <TableHead sx={{ height: "7.3vh" }}>
                  <TableRow>
                    <TableCell id="eMed_config_TableRow1" align="left" sx={{ color: Colors.HeadText }}>
                      {t("SNo")}
                    </TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>
                      {t("LaboratoryName")}
                    </TableCell>
                    <TableCell id="eMed_config_TableRow" align="center" sx={{ color: Colors.HeadText }}>
                      {t("REGNumber")}
                    </TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>
                      {t("Address")}
                    </TableCell>
                    <TableCell id="eMed_config_TableRow" align="center" sx={{ color: Colors.HeadText }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {slice.map((data, index) => {
                  const address =
                    (data.address1 ? data.address1 + ", " : "") +
                    (data.address2 ? data.address2 + ", " : "") +
                    (data.city ? data.city + " - " : "") +
                    (data.pincode ? data.pincode + ", " : "") +
                    (data.state ?? "");
                  const sno = page * rowsPerPage + index + 1;
                  const isSelected = selectedEditID && selectedIndex === index;
                  return (
                      <TableRow key={data.id ?? index} sx={{ bgcolor: isSelected ? "#e2e2e2" : "white" }}>
                        <TableCell align="left">{sno}</TableCell>
                        <TableCell align="left">
                          {(data.outsource_laboratory_name ?? "").length > 15 ? (
                            <Tooltip placement="top" title={data.outsource_laboratory_name} arrow>
                              <div>{data.outsource_laboratory_name ? data.outsource_laboratory_name.slice(0, 15) + "..." : "-"}</div>
                            </Tooltip>
                          ) : (
                            <div>{data.outsource_laboratory_name ?? "-"}</div>
                          )}
                        </TableCell>
                        <TableCell align="center">{data.reg_number ?? "-"}</TableCell>
                        <TableCell align="left">
                          {address.length > 20 ? (
                            <Tooltip placement="top" title={address} arrow>
                              <div>{address ? address.slice(0, 20) + "..." : "-"}</div>
                            </Tooltip>
                          ) : (
                            <div>{address || "-"}</div>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <div className="eMed_TestConfig_Con">
                            <IconButton
                              size="small"
                              disabled={!editAccess}
                              onClick={() => editOutSourceConfigData(data, index)}
                            >
                              <img
                                src={editAccess ? (ImagePaths.Edit?.default ?? ImagePaths.Edit) : (ImagePaths.DisabledEdit?.default ?? ImagePaths.DisabledEdit)}
                                className="eMed_TestConfig_DeleteIcon"
                                alt="Edit"
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={!editAccess}
                              onClick={() => {
                                setDeletedPopup(true);
                                setDeletedID(data.id);
                              }}
                            >
                              <img
                                src={editAccess ? (ImagePaths.Delete?.default ?? ImagePaths.Delete) : (ImagePaths.DisabledDelete?.default ?? ImagePaths.DisabledDelete)}
                                className="eMed_TestConfig_DeleteIcon"
                                alt="Delete"
                              />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                  );
                })}
                </TableBody>
              </Table>
              {filtereddata.length === 0 && (
                <div className="eMed_TestConfig_NoRecords">{t("NoRecordsFound")}</div>
              )}
            </TableContainer>
          </Paper>
          <div className="eMed_TestConfig_LeftPageination">
            <TablePagination
              component="div"
              id="eMed_tbl_pagination"
              rowsPerPageOptions={[10, 20, 30]}
              count={filtereddata.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
        <div className="eMed_TestConfig_RightSide">
          <div className="eMed_TestConfig_RightHead">
            <p className="eMed_Testconfig_RightHead_text">{t("OutsourceConfigration")}</p>
          </div>
          <div className="eMed_TestConfig_RightContentBox">
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("OutsourceLaboratoryName") + " *", "29vw", outsourceLabName, "outsourceLabName")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("RegistrationNo"), "29vw", regNo, "regNo")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderGSTNTextBox(t("GSTNNo"), "29vw")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("Address1"), "29vw", address1, "address1")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("Address2"), "29vw", address2, "address2")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("Pincode*"), "14vw", pincode, "pincode")}
              {renderTextBox(t("City") + " *", "14vw", city, "city")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {renderTextBox(t("State") + " *", "14vw", selectState, "selectState")}
            </div>
          </div>
          <div className="eMed_TestConfig_RightBtnCon">
            <Stack spacing={2} direction="row">
              <Button id="eMed_Btn_Text" variant="outlined" onClick={clearValue}>
                {t("Clear")}
              </Button>
              <Button
                id="eMed_Btn_Text"
                disabled={disableBtn || !editAccess}
                variant="contained"
                onClick={onSave}
              >
                {t("Save")}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {deletedPopup && (
        <DeletePopup
          DeletTitle={t("ConfirmRemovalOutsourceConfig")}
          DeleteNotify={t("effectEntireConfig")}
          DeletContent={t("linkedOutsource")}
          deleteAlertPopupClose={PopupClose}
          removeData={removeDataList}
        />
      )}
      <Loader loaderOpen={isLoader} />
    </div>
  );
}
